import companyService from '../../../services/companyService';
import filterService from '../../../services/filterService';

import {
    setCompanyLoading,
    setCompanyError,
    setCompanyList,
    setCompanyCurrent,
    setCompanyPagination,
    setCompanySort,
    setCompanyFilters,
    setCompanyDefaultFilter,
    setCompanySettings,
    clearCompanyCurrent,
    clearCompanyError,
    setCompanyFilterLoading,
    changeCompanyFilterCondition,
    addCompanyFilterCondition,
    updateCompanyFilterConditionRule,
    updateCompanyFilterConditionValue,
    removeCompanyFilterCondition,
    setCompanySelected,
    setCompanyDownloading,
    resetCompanyFiltersAndPagination,

    setCompanyClassicFilters
} from './companySlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().company.isSettingsInitialized) {
            dispatch(setCompanyLoading(true));
            const response = await companyService.getSettings();

            // Set the settings
            dispatch(setCompanySettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setCompanyClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCompanyDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCompanyError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCompanyLoading(false));
    }
};

export const fetchCompanies = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().company;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setCompanyLoading(true));
        const response = await companyService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setCompanyList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCompanies:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCompanyError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCompanyLoading(false));
    }
};

export const fetchCompany = (id) => async (dispatch) => {
    try {
        dispatch(setCompanyLoading(true));
        const response = await companyService.getOne(id);
        dispatch(setCompanyCurrent(response.data));
    } catch (error) {
        dispatch(setCompanyError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCompanyLoading(false));
    }
};

export const createCompany = (data) => async (dispatch) => {
    try {
        const response = await companyService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCompany = (id, data) => async (dispatch) => {
    try {
        const response = await companyService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCompany = (id) => async (dispatch) => {
    try {
        dispatch(setCompanyLoading(true));
        const response = await companyService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCompanyError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCompanyLoading(false));
    }
};

export const downloadCompany = (type) => async (dispatch, getState) => {
    try {
        const state = getState().company;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setCompanyDownloading(true));
        const response = await companyService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setCompanyDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading companies:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCompanyError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCompanyDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCompanyPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCompanySort(sort));
};

export const searchCompanies = (query) => (dispatch, getState) => {
    const currentFilters = getState().company.filters;
    dispatch(setCompanyFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCompanyDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCompanyFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCompanyFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCompanyFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCompanyFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCompanyFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCompanyFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().company.settings;
            dispatch(setCompanySettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCompanyFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCompanyFilterLoading(false));
        dispatch(setCompanyError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCompanySelected(selectedRows));
    };
};

export const clearCurrentCompany = () => {
    return (dispatch) => {
        dispatch(clearCompanyCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCompanyFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().company.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setCompanyClassicFilters(updatedClassicFilters));
};